import { Component } from 'preact';
import { Mail, Users } from 'lucide-react';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { toast } from 'sonner';
import { Focusable } from './ui/Focusable';
import RuneSpinner from './RuneSpinner';
import AmbientParticles from './AmbientParticles';
import StoreTorchFX from './StoreTorchFX';
import AppHeader from './AppHeader';
import { BackButton } from './ui/navButtons';
import DeckEditorCollection from './DeckEditorCollection';
import DeckEditorSidebar from './DeckEditorSidebar';
import MobileDeckPanel from './mobile/MobileDeckPanel';
import { canMoveToZone } from './DeckEditorSidebar';
import { getImageUrl } from './DeckCardTile';
import { isPhoneLayout } from '../utils/platform';
import { PhoneLongPress } from '../utils/phoneLongPress';
import DeckSidePanel from './DeckSidePanel';
import CardInspector from './CardInspector';
import FoilPreview from './FoilPreview';
import { isFoilFinish } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../utils/sorcery/foilParams.js';
import { Select } from './ui/select';
import { IconChartBar } from './ui/icons';
import { playUI, UI } from '../utils/arena/uiSounds';
import { copyDeckToClipboard } from '../utils/sorcery/deckExport';
import { computeFeaturedCards } from '../utils/sorcery/deckFeaturedCards';
import { shareDeck, unshareDeck, toggleDeckLike } from '../utils/arena/deckLibraryApi';
import { timeAgo } from '../utils/arena/timeFormat';
import DeckStatisticsOverlay from './deckStats/DeckStatisticsOverlay';
import { t, k } from '../localization/t';
import {
  GOLD, GOLD_TEXT, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, PANEL_BG, PANEL_BORDER,
  BEVELED_BTN, GOLD_BTN, DANGER_BTN, INPUT_STYLE, ACCENT_GOLD,
  BG_ATMOSPHERE, VIGNETTE, DIALOG_STYLE,
  TAB_ACTIVE, TAB_INACTIVE, TAB_BAR_STYLE, RADIUS,
  FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';

/* Concentric geometry: the action row sits at the panel's own padding, so
   panel radius = button radius + panel padding (24 = 12 + 12). The copy
   block carries its own inset instead, which is what keeps the buttons
   near the panel edge without cramping the text. */
// Session-constant (see platform.js). Phones get a dedicated layout via
// renderPhone(): compact header, full-bleed collection, MobileDeckPanel.
const PHONE = isPhoneLayout();

const UNSAVED_PANEL_RADIUS = PHONE ? '1rem' : '1.5cqw';
const UNSAVED_BTN_RADIUS = PHONE ? '0.5rem' : '0.75cqw';

/* Every action shares one geometry. Same height, width, radius and label
   size. So the three read as one row. Hierarchy comes from the fill
   (muted / danger / gold), not from size. */
const UNSAVED_BTN = {
  borderRadius: UNSAVED_BTN_RADIUS,
  height: PHONE ? '2.5rem' : '2.5cqw',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const UNSAVED_BTN_CLASS = 'flex-1 whitespace-nowrap text-xs font-semibold '
  + 'transition-[transform,filter] duration-200 hover:scale-[1.03] hover:brightness-110 active:scale-[0.96]';

/* Loading-gate fade-out. Must match `.entrance-gate.is-closing` in app.css.
   the gate unmounts on this timer, so a shorter value clips the fade. */
const GATE_FADE_MS = 180;

/**
 * "Save before leaving?" confirmation. Own component so mount/unmount owns
 * the Escape registration and the enter animation runs once per open.
 */
class UnsavedChangesDialog extends Component {
  componentDidMount() {
    this._escDismissId = pushDismiss(() => this.props.onCancel());
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
  }

  render() {
    const { onCancel, onDiscard, onSave } = this.props;
    return (
      <div
        className="dialog-backdrop-in fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm"
        onClick={onCancel}
      >
        <div
          className="dialog-panel-in relative w-[30cqw] max-w-[90cqw] p-3"
          style={{ ...DIALOG_STYLE, borderRadius: UNSAVED_PANEL_RADIUS, ...(PHONE ? { width: '22rem', maxWidth: '92vw' } : {}) }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners radius={UNSAVED_PANEL_RADIUS} size="1.75cqw" />
          <div className="px-3 pt-3">
            <h2
              className="dialog-item-in arena-heading text-xl font-bold mb-2"
              style={{ color: TEXT_PRIMARY, textWrap: 'balance', textShadow: '0 2px 4px rgba(0,0,0,0.5)', '--stagger': '70ms' }}
            >
              {t('Unsaved Changes')}
            </h2>
            <p
              className="dialog-item-in text-sm leading-relaxed"
              style={{ color: TEXT_BODY, textWrap: 'pretty', '--stagger': '130ms' }}
            >
              {t('You have unsaved changes. Save before leaving?')}
            </p>
          </div>
          <div className="dialog-item-in mt-4 mb-3" style={{ '--stagger': '170ms' }}>
            <OrnamentalDivider />
          </div>
          <div className="dialog-item-in flex items-stretch gap-2" style={{ '--stagger': '210ms' }}>
            <button
              type="button"
              className={UNSAVED_BTN_CLASS}
              style={{ ...BEVELED_BTN, ...UNSAVED_BTN, color: TEXT_BODY }}
              onClick={() => { playUI(UI.CANCEL); onCancel(); }}
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              className={UNSAVED_BTN_CLASS}
              style={{ ...DANGER_BTN, ...UNSAVED_BTN }}
              onClick={() => { playUI(UI.DELETE); onDiscard(); }}
            >
              {t('Discard')}
            </button>
            <button
              type="button"
              className={UNSAVED_BTN_CLASS}
              style={{ ...GOLD_BTN, ...UNSAVED_BTN }}
              onClick={() => { playUI(UI.CONFIRM); onSave(); }}
            >
              {t('Save & Leave')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export const GAME_MODE_OPTIONS = [
  { value: 'constructed', label: k('Constructed'), description: k('Ranked-eligible. Only cards you own.'), freePlayDescription: k('Ranked-eligible. Every card is allowed.') },
  { value: 'peasant', label: k('Peasant'), description: k('Ranked-eligible. Ordinary & Exceptional cards only.') },
  { value: 'sandbox', label: k('Sandbox'), description: k("Anything goes. Practice with cards you don't own."), freePlayHidden: true },
];

// The formats a deck can be saved as right now. Free play has no Sandbox:
// every card is already legal in Constructed.
export function gameModeOptions() {
  if (!isFreePlay()) return GAME_MODE_OPTIONS;
  return GAME_MODE_OPTIONS
    .filter((o) => !o.freePlayHidden)
    .map((o) => (o.freePlayDescription ? { ...o, description: o.freePlayDescription } : o));
}

const VALID_GAME_MODES = new Set(GAME_MODE_OPTIONS.map((o) => o.value));
import { buildOwnedMap, buildUsedMap } from '../utils/arena/collectionUtils';
import { ART_GROUP_BASE, artGroupOf } from '../utils/sorcery/artGroups';
import { canAddCard, resolveCardZone, validateDeckStructure, applyAvatarZoneRouting, getMissingCards, getInvalidEntries, isPeasantLegal, effectiveDeckMode } from '../utils/sorcery/deckRules';
import { isFreePlay } from '../utils/freePlay';
import TutorialOverlay from './TutorialOverlay';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../utils/arena/tutorialState';

const DECK_TUTORIAL_KEY = 'deck-builder';

const DECK_TUTORIAL_STEPS = [
  {
    key: 'welcome',
    title: k('Welcome to the Deck Builder'),
    body: k('This is where you craft the decks you bring into matches. Cards you own are browsable on the left, and your current deck builds in the sidebar on the right.'),
    freePlayBody: k('This is where you craft the decks you bring into matches. Every card is browsable on the left, and your current deck builds in the sidebar on the right.'),
  },
  {
    key: 'collection',
    title: k('Your Collection'),
    freePlayTitle: k('Card Pool'),
    body: k('Every card you own sits here, grouped by rarity and filterable by element, type, set, and owned vs. all. Use the scope toggle (In Deck / Owned / All Cards) to control which cards appear. Click a card to add it to your deck; right-click to remove one copy.'),
    freePlayBody: k('Every card sits here, grouped by rarity and filterable by element, type and set. Use the scope toggle (In Deck / All) to control which cards appear. Click a card to add it to your deck; right-click to remove one copy.'),
    selector: '[data-tutorial="deck-collection"]',
  },
  {
    key: 'sidebar',
    title: k('Deck Sidebar'),
    body: k('Your active deck lives here, split by zone (Avatar, Spellbook, Atlas, Sideboard). Cards show their ownership count vs. how many you have in the deck. Hover any card to see a big preview; click the + / − buttons to adjust copies.'),
    freePlayBody: k('Your active deck lives here, split by zone (Avatar, Spellbook, Atlas, Sideboard). Hover any card to see a big preview; click the + / − buttons to adjust copies.'),
    selector: '[data-tutorial="deck-sidebar"]',
  },
  {
    key: 'save',
    title: k('Save Your Deck'),
    body: k('When you\'re happy with the build, hit Save Deck up in the header. Decks are saved to your account and available in every future match. You can keep multiple decks and pick one at matchmaking time.'),
    selector: '[data-tutorial="deck-save"]',
  },
  {
    key: 'deckrules',
    title: k('Deck Rules'),
    body: k('Sorcery decks have rarity limits: up to 4 copies of Ordinary cards, 3 Exceptional, 2 Elite, and 1 Unique. Avatars go in their own zone, and Sites go in your Atlas. The builder will let you know if a deck breaks the rules.'),
  },
];

// Content fingerprint of everything a save persists. Dirty state is
// "current fingerprint differs from the last saved one", so remove-then-
// re-add nets out to clean.
function deckSnapshot(state) {
  const cards = state.chosenCards
    .map((e) => `${e.card?.unique_id}|${e.printing?._source_printing_id || e.printing?.unique_id}|${e.zone || ''}`)
    .sort();
  return JSON.stringify([cards, state.deckName, state.gameMode, state.strategyNotes]);
}

export default class DeckEditor extends Component {
  constructor(props) {
    super(props);
    const deck = this.props.deck || {};
    const isLibraryView = !!deck.libraryView;
    this.state = {
      chosenCards: applyAvatarZoneRouting(
        (deck.cards || []).map((entry) => {
          if (entry.zone) return entry;
          const cat = entry.card?._sorceryCategory;
          let zone = 'spellbook';
          if (cat === 'Site') zone = 'atlas';
          else if (cat === 'Avatar') zone = 'avatar';
          else if (entry.isSideboard) zone = 'collection';
          return { ...entry, zone };
        }),
      ),
      deckName: deck.name || '',
      deckId: deck.id || '',
      // Library-view decks always open in sandbox so the collection
      // grid hides "you don't own this" noise. Confirmed scope:
      // owning the cards is irrelevant when previewing someone else's
      // deck, and the user can switch to Constructed later if they
      // save and decide to play it ranked.
      gameMode: isLibraryView ? effectiveDeckMode('sandbox') : effectiveDeckMode(deck.gameMode),
      isSaving: false,
      showStats: false,
      showUnsavedPrompt: false,
      // Loading gate. The editor mounts fully. The collection grid has to lay
      // out and measure, and its first screenful of card art has to warm.
      // but stays invisible behind a spinner until DeckEditorCollection reports
      // that art is ready. Only then does the entrance cascade run, so it
      // animates real cards in rather than a grid of empty placeholders.
      contentReady: false,
      // Trails contentReady by one fade so the gate can animate out.
      gateMounted: true,
      hoveredSidebarCard: null,
      hoveredSidebarRect: null,
      // WebGL foil overlay for the sidebar hover preview. { imageUrl, zones }
      // when a foil card is previewed, null otherwise. Set only when the foil
      // identity changes (not every rAF frame) so the FoilCanvas isn't churned.
      previewFoil: null,
      // Captured rect for the "why can't I save?" tooltip that
      // appears when the user hovers the disabled Save button while
      // the deck has format-illegal cards. Stored as a viewport rect
      // captured at mouseenter time so the tooltip can be positioned
      // without re-querying the DOM on every render.
      saveBlockedHoverRect: null,
      showDeckTutorial: false,
      showShareMenu: false,
      deckShared: deck.shared || false,
      strategyNotes: deck.strategyNotes || '',
      showSidePanel: false,
      sidePanelInitialTab: 'strategy',
      libraryLikes: deck.likes || 0,
      libraryLiked: !!deck.liked,
      libraryToggleBusy: false,
      // Card opened from an @card link inside the notes side panel.
      // null when no inspector is open.
      notesInspectorCard: null,
      // Deck entry opened by pressing Space over a sidebar row.
      sidebarInspect: null,
    };
    this._savedSnapshot = deckSnapshot(this.state);
  }

  handleToggleShare = async () => {
    this.setState({ showShareMenu: false });
    const deckId = this.state.deckId;
    if (!deckId) {
      toast.error(t('Save the deck first before sharing'));
      return;
    }
    try {
      if (this.state.deckShared) {
        await unshareDeck(deckId);
        this.setState({ deckShared: false });
        toast.success(t('Deck removed from library'));
      } else {
        await shareDeck(deckId);
        this.setState({ deckShared: true });
        toast.success(t('Deck shared to library'));
      }
    } catch {
      toast.error(t('Failed to update sharing'));
    }
  };

  handleCopyForCuriosa = async () => {
    this.setState({ showShareMenu: false });
    try {
      await copyDeckToClipboard(this.state.chosenCards);
      toast.success(t('Deck list copied to clipboard'));
    } catch {
      toast.error(t('Failed to copy deck list'));
    }
  };

  componentDidMount() {
    // Comment deep-link: land with the side panel already open on comments.
    if (this.props.highlightCommentId) {
      this.setState({ showSidePanel: true, sidePanelInitialTab: 'comments' });
    }
    // First-run onboarding. Waits on hydration so the on-disk seen flag is
    // read first; the timer itself is armed by the loading gate lifting.
    // Skipped on phone: the tutorial steps spotlight desktop anchors.
    if (PHONE) return;
    const profileId = this.props.arenaProfile?.id;
    if (profileId) {
      hydrateTutorialState().then(() => {
        if (this._unmounted) return;
        if (!shouldAutoPlay(profileId, DECK_TUTORIAL_KEY)) return;
        this._tutorialEligible = true;
        this.maybeStartTutorial();
      });
    }
  }

  /**
   * Arms the onboarding overlay once BOTH the seen-flag hydration has resolved
   * and the loading gate has lifted. Either can land first. Gating on the
   * reveal matters: the overlay measures `[data-tutorial]` elements to cut its
   * spotlight, and while the gate is up those elements are laid out but held at
   * opacity 0, so it would spotlight things the user cannot see. The extra
   * delay lets the entrance cascade settle before the spotlight lands.
   */
  maybeStartTutorial() {
    if (!this._tutorialEligible || !this.state.contentReady || this._tutorialTimer) return;
    this._tutorialTimer = setTimeout(() => {
      this._tutorialTimer = null;
      if (!this._unmounted) this.setState({ showDeckTutorial: true });
    }, 500);
  }

  componentDidUpdate(prevProps, prevState) {
    // A fresh comment deep-link while already mounted re-opens the panel.
    const hl = this.props.highlightCommentId;
    if (hl && hl !== prevProps.highlightCommentId) {
      this.setState({ showSidePanel: true, sidePanelInitialTab: 'comments' });
    }
    // Notes-panel card inspector owns Escape via the central stack while open.
    if (!prevState.notesInspectorCard && this.state.notesInspectorCard) {
      this._escDismissId = pushDismiss(() => this.handleCloseNotesInspector());
    } else if (prevState.notesInspectorCard && !this.state.notesInspectorCard) {
      removeDismiss(this._escDismissId);
    }
    // Sync deck ID from parent when it changes (e.g., after first save assigns an ID)
    const newId = this.props.deck?.id;
    if (newId && newId !== prevProps.deck?.id && newId !== this.state.deckId) {
      this.setState({ deckId: newId });
    }
    // Sync strategyNotes from parent after a save that returned updated
    // server state. Keeps the side panel's read view in lockstep with
    // the persisted value. Only adopt the parent's value when we don't
    // have local unsaved changes to avoid stomping in-progress edits.
    const newNotes = this.props.deck?.strategyNotes;
    if (
      newNotes !== undefined &&
      newNotes !== prevProps.deck?.strategyNotes &&
      newNotes !== this.state.strategyNotes &&
      !this.hasUnsavedChanges()
    ) {
      this.setState({ strategyNotes: newNotes }, () => { this._savedSnapshot = deckSnapshot(this.state); });
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    removeDismiss(this._escDismissId);
    if (this._tutorialTimer) clearTimeout(this._tutorialTimer);
    if (this._gateTimer) clearTimeout(this._gateTimer);
  }

  handleDeckTutorialDismiss = () => {
    const profileId = this.props.arenaProfile?.id;
    if (profileId) markTutorialSeen(profileId, DECK_TUTORIAL_KEY);
    this.setState({ showDeckTutorial: false });
  };

  // Find the avatar in the primary slot. Imposter decks can carry
  // additional avatars in the spellbook or collection, so we identify
  // the primary by zone, not just type. Fall back to a type-only lookup
  // when no entry yet has a zone (legacy/import path before routing).
  getCurrentAvatar = () => {
    const cards = this.state.chosenCards;
    return (
      cards.find((e) => e.card?.type === 'Avatar' && e.zone === 'avatar')?.card
      || cards.find((e) => e.card?.type === 'Avatar' && !e.zone)?.card
      || null
    );
  };

  // Centralised mutator. Every chosenCards write goes through here so a
  // single rule. "the array's zones always reflect the current avatar's
  // rules." Is invariant across add, decrement, drag, hotkey. The
  // routing step is the same helper used on deck load and import.
  //
  // Also detects when a user mutation newly invalidates entries (the
  // Imposter-removal case: extra avatars in spellbook/collection that
  // were legal under Imposter become illegal under a different avatar).
  // We toast once per transition so the red highlight doesn't sneak up
  // on the user.
  setChosenCards = (mutator) => {
    this.setState((state) => {
      const next = mutator(state.chosenCards);
      if (!next) return null;
      const routed = applyAvatarZoneRouting(next);

      const prevAvatar = state.chosenCards.find((e) => e.card?.type === 'Avatar' && e.zone === 'avatar')?.card
        || state.chosenCards.find((e) => e.card?.type === 'Avatar' && !e.zone)?.card
        || null;
      const nextAvatar = routed.find((e) => e.card?.type === 'Avatar' && e.zone === 'avatar')?.card
        || routed.find((e) => e.card?.type === 'Avatar' && !e.zone)?.card
        || null;
      const prevInvalid = getInvalidEntries(state.chosenCards, prevAvatar);
      const nextInvalid = getInvalidEntries(routed, nextAvatar);
      // Only the stranded-avatar transition needs a heads-up toast. It
      // happens on avatar swap and is easy to miss. Unpaired-Unique flags
      // fire while the user is actively building a Duplicator deck, where
      // the red sidebar tint + save-time validator are already obvious.
      const newlyStrandedAvatars = [...nextInvalid.keys()].filter((id) =>
        !prevInvalid.has(id) && nextInvalid.get(id)?.kind === 'extra_avatar',
      ).length;
      if (newlyStrandedAvatars > 0) {
        // Defer the toast so it fires after setState commits. Avoids
        // calling showToast inside a state updater (React frowns on it
        // and our toast system reads the latest state).
        queueMicrotask(() => this.showToast(
          newlyStrandedAvatars === 1
            ? t('Heads up: 1 avatar in your deck is now invalid. Re-equip Imposter or remove it to save.')
            : t('Heads up: {n} avatars in your deck are now invalid. Re-equip Imposter or remove them to save.', { n: newlyStrandedAvatars }),
        ));
      }

      return { chosenCards: routed };
    });
  };

  handleAddCard = (card, specificPrinting) => {
    const ownedMap = this.props.arenaProfile ? buildOwnedMap(this.props.arenaProfile.collection) : null;
    const usedElsewhereMap = buildUsedMap(this.props.savedDecks || [], this.state.deckId);
    const avatar = this.getCurrentAvatar();
    // Imposter's allowExtraAvatars rule is enforced inside canAddCard
    // (via countTowardLimit). Adding a second Avatar with Imposter
    // equipped is legal because the singleton check ignores entries
    // outside the avatar slot. resolveCardZone uses primaryAvatarPresent
    // to route that second Avatar to the spellbook by default.
    const targetZone = resolveCardZone(card, avatar, {
      primaryAvatarPresent: card.type === 'Avatar' && !!avatar,
    });
    const check = canAddCard(card, this.state.chosenCards, {
      ownedMap, usedElsewhereMap,
      avatar,
      targetZone,
      gameMode: this.state.gameMode,
    });
    if (!check.allowed) {
      this.showToast(check.reason);
      return;
    }

    const printing = specificPrinting || card.printings?.[card.printings.length - 1] || card.printings?.[0];
    if (!printing) return;

    const zone = targetZone;

    this.setChosenCards((chosen) => [
      ...chosen,
      { card, printing, zone, isSideboard: false },
    ]);
  };

  handleChangeZone = (cardUniqueId, newZone, artGroup = ART_GROUP_BASE, foiling = 'S') => {
    this.setChosenCards((chosen) => {
      // Pick an entry that isn't already in the target zone. The
      // sidebar aggregates rows by (cardId, artGroup, foiling), so a plain
      // findIndex would happily land on an entry already in newZone
      // once any copy has been moved there. Turning every subsequent
      // move into a no-op (the symptom being "I can only move a card
      // to the collection once").
      const idx = chosen.findIndex((c) =>
        c.card.unique_id === cardUniqueId &&
        artGroupOf(c.printing) === artGroup &&
        (c.printing?.foiling || 'S') === foiling &&
        c.zone !== newZone,
      );
      if (idx === -1) return null;
      const next = [...chosen];
      next[idx] = { ...next[idx], zone: newZone, isSideboard: newZone === 'collection' };
      return next;
    });
  };

  handleAddCardToZone = (card, zone, specificPrinting) => {
    const printing = specificPrinting || card.printings?.[0];
    if (!printing) return;

    const ownedMap = this.props.arenaProfile ? buildOwnedMap(this.props.arenaProfile.collection) : null;
    const usedElsewhereMap = buildUsedMap(this.props.savedDecks || [], this.state.deckId);
    const avatar = this.getCurrentAvatar();
    const check = canAddCard(card, this.state.chosenCards, {
      ownedMap, usedElsewhereMap,
      avatar,
      targetZone: zone,
      gameMode: this.state.gameMode,
    });
    if (!check.allowed) {
      this.showToast(check.reason);
      return;
    }

    this.setChosenCards((chosen) => [
      ...chosen,
      { card, printing, zone, isSideboard: zone === 'collection' },
    ]);
  };

  // Animated sidebar card preview state
  _previewAnim = { opacity: 0, top: 0, targetTop: 0, visible: false, imgUrl: '', cardName: '', isSite: false, isFoil: false, foiling: '', sidebarLeft: 0 };
  _previewRafId = null;

  // Instance field, not state: read by the collection grid's Space handler
  // (via getSidebarHovered) so a press over a sidebar row doesn't also arm
  // the grid's pending-open. Storing it in state would re-render the whole
  // editor on every row hover.
  _sidebarHovered = false;

  getSidebarHovered = () => this._sidebarHovered;

  // Resolve a sidebar row (card id + art group + foiling) back to its entry.
  findSidebarEntry(cardUniqueId, artGroup, foiling) {
    const targetGroup = artGroup || ART_GROUP_BASE;
    const targetFoiling = foiling || 'S';
    const { chosenCards } = this.state;
    return chosenCards.find((c) =>
      c.card.unique_id === cardUniqueId
      && artGroupOf(c.printing) === targetGroup
      && (c.printing?.foiling || 'S') === targetFoiling
    ) || chosenCards.find((c) => c.card.unique_id === cardUniqueId) || null;
  }

  handleSidebarInspect = (cardUniqueId, artGroup, foiling) => {
    const entry = this.findSidebarEntry(cardUniqueId, artGroup, foiling);
    if (entry) this.setState({ sidebarInspect: entry });
  };

  handleCloseSidebarInspect = () => {
    this.setState({ sidebarInspect: null });
  };

  handleSidebarHover = (cardUniqueId, hoverArtGroup, hoverFoiling, rowElement) => {
    this._sidebarHovered = !!cardUniqueId;
    if (!cardUniqueId) {
      this._previewAnim.visible = false;
      if (this.state.previewFoil) this.setState({ previewFoil: null });
      this._startPreviewLoop();
      return;
    }
    const targetGroup = hoverArtGroup || ART_GROUP_BASE;
    const targetFoiling = hoverFoiling || 'S';
    const card = this.findSidebarEntry(cardUniqueId, targetGroup, targetFoiling);
    if (!card) return;
    // Prefer the exact hovered element (one card+art+foiling can render in
    // both Spellbook and Collection rows; falling back to querySelector would
    // pick whichever one is first in the DOM and mispin the preview).
    const row = rowElement
      || document.querySelector(`[data-sidebar-card-id="${cardUniqueId}"][data-sidebar-art-group="${targetGroup}"][data-sidebar-foiling="${targetFoiling}"]`)
      || document.querySelector(`[data-sidebar-card-id="${cardUniqueId}"]`);
    const rect = row?.getBoundingClientRect();
    if (!rect) return;

    // The preview element has `position: fixed` and is rendered inside
    // `.app-canvas`, which is the containing block (transform: translateZ(0)
    // for letterbox containment). So the top/left we apply are canvas-local,
    // not viewport-local. Convert the row rect by subtracting the canvas's
    // viewport offset, and clamp against canvas height instead of innerHeight
    // so the preview stays inside the canvas at any letterbox state.
    const canvasEl = document.querySelector('.app-canvas');
    const canvasRect = canvasEl ? canvasEl.getBoundingClientRect() : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
    const canvasH = canvasRect.height;
    const localCenterY = (rect.top - canvasRect.top) + rect.height / 2;
    const localLeft = rect.left - canvasRect.left;

    const imgUrl = card.printing?.image_url || card.card?.printings?.[0]?.image_url || '';
    const isSite = card.card?.played_horizontally;
    const foiling = card.printing?.foiling || 'S';
    const isFoil = foiling === 'F' || foiling === 'R';
    // Size the preview in cqw (1cqw = canvasWidth/100) so it scales with the
    // canvas exactly like the grid cards do. A fixed px size looked tiny next
    // to the cqw-sized grid on large windows. PREVIEW_CQW is the portrait card
    // width; a big, readable preview (clearly larger than a grid tile). Sites
    // are landscape, so their long edge is the width.
    const PREVIEW_CQW = 20;
    const cqwPx = canvasRect.width / 100;
    const portraitWpx = PREVIEW_CQW * cqwPx;
    const portraitHpx = portraitWpx * (88 / 63);
    const previewW = isSite ? portraitHpx : portraitWpx;
    const previewH = isSite ? portraitWpx : portraitHpx;
    let targetTop = localCenterY - previewH / 2;
    if (targetTop < 8) targetTop = 8;
    if (targetTop + previewH > canvasH - 8) targetTop = canvasH - 8 - previewH;

    const anim = this._previewAnim;
    const wasVisible = anim.visible;
    anim.visible = true;
    anim.imgUrl = imgUrl;
    anim.cardName = card.card.name;
    anim.isSite = isSite;
    anim.isFoil = isFoil;
    anim.foiling = foiling;
    anim.sidebarLeft = localLeft;
    anim.targetTop = targetTop;
    anim.previewW = previewW;
    anim.previewH = previewH;
    // Snap straight to the hovered row instead of easing toward it. The lerp in
    // _tickPreview made the preview visibly slide between rows and lag behind a
    // fast scroll (the "sorts to the side" feel); snapping tracks the cursor
    // row exactly. Opacity still fades in on first appearance.
    anim.top = targetTop;
    if (!wasVisible) anim.opacity = 0;
    // Premium WebGL foil overlay rides the (rAF-sized) preview wrapper. Only
    // the 'F' finish uses the new foil; rainbow ('R') keeps its CSS treatment.
    // Reconcile state only when the foil identity changes so the FoilCanvas
    // isn't remounted while the preview animates.
    const wantFoil = isFoilFinish(foiling) && imgUrl
      ? { imageUrl: imgUrl, zones: foilZonesForCard(card.card), isSite }
      : null;
    const cur = this.state.previewFoil;
    if ((cur?.imageUrl || null) !== (wantFoil?.imageUrl || null)) {
      this.setState({ previewFoil: wantFoil });
    }
    this._startPreviewLoop();
  };

  _startPreviewLoop = () => {
    if (this._previewRafId) return;
    this._previewRafId = requestAnimationFrame(this._tickPreview);
  };

  _tickPreview = () => {
    this._previewRafId = null;
    const anim = this._previewAnim;
    const speed = 0.18;

    if (anim.visible) {
      anim.opacity += (1 - anim.opacity) * speed;
      if (anim.opacity > 0.99) anim.opacity = 1;
    } else {
      anim.opacity += (0 - anim.opacity) * (speed * 1.5);
      if (anim.opacity < 0.01) anim.opacity = 0;
    }

    anim.top += (anim.targetTop - anim.top) * speed;

    const el = this._previewRef;
    if (el) {
      const previewW = anim.previewW || 300;
      const previewH = anim.previewH || 420;
      const gap = 16;
      const left = anim.sidebarLeft - previewW - gap;
      el.style.opacity = String(anim.opacity);
      el.style.top = `${anim.top}px`;
      el.style.left = `${left}px`;
      el.style.width = `${previewW}px`;
      el.style.height = `${previewH}px`;
      el.style.transform = `scale(${0.85 + anim.opacity * 0.15})`;
      el.style.display = anim.opacity > 0.01 ? 'block' : 'none';

      const inner = el.querySelector('[data-preview-inner]');
      if (inner) inner.className = 'w-full h-full rounded-card card-mask';

      const img = el.querySelector('img');
      if (img && img.src !== anim.imgUrl && anim.imgUrl) img.src = anim.imgUrl;
      if (img) {
        img.alt = anim.cardName;
        if (anim.isSite) {
          img.style.transform = 'rotate(90deg)';
          img.style.transformOrigin = 'center center';
          img.style.width = `${previewH}px`;
          img.style.height = `${previewW}px`;
          img.style.position = 'absolute';
          img.style.top = `${(previewH - previewW) / 2}px`;
          img.style.left = `${(previewW - previewH) / 2}px`;
        } else {
          img.style.transform = 'none';
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.position = 'static';
          img.style.top = '';
          img.style.left = '';
        }
      }
    }

    const isSettled = anim.visible ? anim.opacity >= 1 && Math.abs(anim.top - anim.targetTop) < 0.5
      : anim.opacity <= 0;
    if (!isSettled) {
      this._previewRafId = requestAnimationFrame(this._tickPreview);
    }
  };

  renderSidebarPreview() {
    return (
      <div
        ref={(el) => { this._previewRef = el; }}
        className="fixed pointer-events-none z-[60]"
        style={{
          display: 'none',
          opacity: 0,
          borderRadius: RADIUS.card,
          boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 30px rgba(0,0,0,0.4)',
          transformOrigin: 'right center',
          willChange: 'transform, opacity, top',
        }}
      >
        <div data-preview-inner className="w-full h-full rounded-card card-mask" style={{ position: 'relative' }}>
          <img src="" alt="" className="object-cover" draggable={false} />
          {this.state.previewFoil ? (
            <FoilPreview
              imageUrl={this.state.previewFoil.imageUrl}
              zones={this.state.previewFoil.zones}
              params={DEFAULT_FOIL_PARAMS}
              rotate90={this.state.previewFoil.isSite}
            />
          ) : null}
        </div>
      </div>
    );
  }

  showToast = (message) => {
    toast(message);
  };

  handleIncrement = (cardUniqueId, artGroup = ART_GROUP_BASE, foiling = 'S') => {
    const existing = this.state.chosenCards.find(c =>
      c.card.unique_id === cardUniqueId
      && artGroupOf(c.printing) === artGroup
      && (c.printing?.foiling || 'S') === foiling
    );
    if (!existing) return;
    playUI(UI.EQUIP);
    this.handleAddCard(existing.card, existing.printing);
  };

  handleDecrement = (cardUniqueId, artGroup = ART_GROUP_BASE, foiling = 'S') => {
    playUI(UI.UNEQUIP);
    this.setChosenCards((chosen) => {
      const idx = chosen.findLastIndex(c =>
        c.card.unique_id === cardUniqueId
        && artGroupOf(c.printing) === artGroup
        && (c.printing?.foiling || 'S') === foiling
      );
      if (idx === -1) return null;
      const next = [...chosen];
      next.splice(idx, 1);
      return next;
    });
  };

  handleSave = async () => {
    if (this.state.isSaving || this.state.chosenCards.length === 0) return;

    // Whole-deck structural validation (avatar-aware + format-aware).
    // Handles rules that can't be enforced per-add. Duplicator's pair
    // / uniques-only requirements, peasant-format rarity, anything
    // else that needs a full-deck view. Per-add constraints are
    // already gated in handleAddCard via canAddCard.
    const avatar = this.getCurrentAvatar();
    // Pass the full deck (including the collection sideboard) so the
    // Imposter rule. Which is *about* extras outside the primary
    // slot. Can see them. validateDeckStructure filters the sideboard
    // out internally for the rules that should ignore it (pairs).
    const structure = validateDeckStructure(
      this.state.chosenCards,
      avatar,
      { gameMode: this.state.gameMode },
    );
    if (!structure.valid) {
      this.showToast(structure.errors[0]);
      return;
    }

    this.setState({ isSaving: true });
    try {
      const cards = this.state.chosenCards.map(entry => {
        const cardId = entry.card?.unique_id;
        const printingId = entry.printing?._source_printing_id || entry.printing?.unique_id;
        if (!cardId || !printingId) return null;
        // Persist the player's explicit zone choice. The type-based
        // default (Site → atlas, Avatar → avatar) is just a convenient
        // starting point in the editor. Some avatars (Magician etc.)
        // let the player slot sites into the spellbook, and that
        // assignment must survive the save so the match-engine sees
        // the configured pile layout instead of re-inferring it.
        return {
          cardId,
          cardName: entry.card.name,
          printingId,
          zone: entry.zone || null,
          isSideboard: entry.zone === 'collection',
        };
      }).filter(Boolean);

      const sorted = [...this.state.chosenCards].sort((a, b) => {
        const aAvatar = a.zone === 'avatar' || a.card?._sorceryCategory === 'Avatar' ? 0 : 1;
        const bAvatar = b.zone === 'avatar' || b.card?._sorceryCategory === 'Avatar' ? 0 : 1;
        return aAvatar - bAvatar;
      });
      const seen = new Set();
      const previewCards = [];
      for (const entry of sorted) {
        const id = entry.card?.unique_id;
        if (!id || seen.has(id)) continue;
        const url = entry.printing?.image_url || '';
        if (!url || url.startsWith('blob:')) continue;
        seen.add(id);
        previewCards.push({ name: entry.card.name, imageUrl: url });
        if (previewCards.length >= 10) break;
      }

      const isLibraryView = !!this.props.deck?.libraryView;

      // Save-to-Library auto-picks the game mode by ownership. If the
      // player owns every card in the deck the saved copy becomes
      // constructed (ranked-eligible by default); otherwise it stays
      // sandbox so the deck remains playable while they collect the
      // missing pieces. Owner saves keep the user's explicit toggle.
      // Peasant is preserved as-is from the editor. The rarity rule
      // is independent of ownership and was an explicit user choice.
      let saveGameMode = this.state.gameMode;
      if (isLibraryView && saveGameMode !== 'peasant') {
        const ownedMap = this._getOwnedMap();
        const { count: missingCount } = getMissingCards(this.state.chosenCards, ownedMap);
        saveGameMode = missingCount === 0 ? 'constructed' : 'sandbox';
      }

      const featuredCards = computeFeaturedCards(cards, this.props.sorceryCards, 3);

      const payload = {
        // In library view the editor is acting as a sandbox preview of
        // someone else's deck. Saving always creates a NEW owned deck
        // for the current player rather than updating the source row.
        ...(this.state.deckId && !isLibraryView ? { id: this.state.deckId } : {}),
        name: this.state.deckName.trim() || 'Untitled Deck',
        format: 'constructed',
        // Tags aren't editable here; round-trip whatever the deck was
        // opened with so a save doesn't drop them. Library previews
        // carry no tags, so their saved copies start untagged.
        tags: this.props.deck?.tags ?? [],
        gameMode: saveGameMode,
        cards,
        previewCards,
        featuredCards,
        strategyNotes: this.state.strategyNotes || '',
      };

      const result = await this.props.onSave(payload);
      this.setState({
        isSaving: false,
        deckId: result?.id || this.state.deckId,
        deckName: result?.name || this.state.deckName,
      }, () => { this._savedSnapshot = deckSnapshot(this.state); });
    } catch (err) {
      console.error('Failed to save deck:', err);
      this.setState({ isSaving: false });
    }
  };

  // Lifts the loading gate. Fires once, from the collection's art preload.
  // The gate lingers one fade-out longer than the flag so the spinner
  // crossfades with the entrance cascade instead of cutting to nothing.
  handleContentReady = () => {
    if (this.state.contentReady) return;
    this.setState({ contentReady: true }, () => this.maybeStartTutorial());
    this._gateTimer = setTimeout(() => {
      this._gateTimer = null;
      if (!this._unmounted) this.setState({ gateMounted: false });
    }, GATE_FADE_MS);
  };

  hasUnsavedChanges = () => deckSnapshot(this.state) !== this._savedSnapshot;

  handleBack = () => {
    if (this.hasUnsavedChanges()) {
      this.setState({ showUnsavedPrompt: true });
      return;
    }
    this.props.onBack();
  };

  handleSaveAndLeave = async () => {
    await this.handleSave();
    this.setState({ showUnsavedPrompt: false });
    this.props.onBack();
  };

  // Memoize the derived ownedMap by the collection reference. Without
  // this cache, buildOwnedMap() returns a new Map on every render, which
  // propagates into DeckEditorCollection.scopeData → CardFilterBar's
  // componentDidUpdate → emitChange → handleFilterChange → resetScroll.
  // The result was: scrolling the card grid would snap back to the top
  // every time DeckEditor re-rendered (viewport-scale tick, tutorial
  // timer, etc.). Caching by reference keeps scopeData stable so the
  // filter bar only re-emits when something the player actually changed.
  _getOwnedMap() {
    const collection = this.props.arenaProfile?.collection;
    if (this._ownedMapInputCollection === collection && this._ownedMapCache) {
      return this._ownedMapCache;
    }
    this._ownedMapInputCollection = collection;
    this._ownedMapCache = collection ? buildOwnedMap(collection) : new Map();
    return this._ownedMapCache;
  }

  // Unique element identities present in the current deck, sorted
  // canonically (Fire / Water / Earth / Air) so the pills always
  // appear in the same order regardless of how the cards were added.
  // Sourced from chosenCards (the editor's live state) so additions
  // / removals while building reflect in the notes panel header.
  getDeckElements = () => {
    const order = { Fire: 1, Water: 2, Earth: 3, Air: 4 };
    const set = new Set();
    for (const entry of this.state.chosenCards) {
      const els = entry.card?.elements;
      if (Array.isArray(els)) {
        for (const el of els) if (el?.name) set.add(el.name);
      }
    }
    return [...set].sort((a, b) => (order[a] || 99) - (order[b] || 99));
  };

  setGameMode = (mode) => {
    if (!VALID_GAME_MODES.has(mode)) return;
    if (this.state.gameMode === mode) return;
    playUI(UI.HOVER, { volume: 0.5 });
    this.setState({ gameMode: mode });
  };

  handleToggleSidePanel = (initialTab) => {
    this.setState((s) => ({
      showSidePanel: !s.showSidePanel,
      sidePanelInitialTab: initialTab || s.sidePanelInitialTab || 'strategy',
    }));
  };

  // Open the card inspector for an @card link inside the notes panel.
  // The link only carries the card's unique_id, so we resolve to the
  // full catalog entry here and stash it for the render.
  handleOpenCardFromNotes = (card) => {
    if (!card) return;
    this.setState({ notesInspectorCard: card });
  };

  handleCloseNotesInspector = () => {
    this.setState({ notesInspectorCard: null });
  };

  handleStrategyNotesChange = (notes) => {
    this.setState({ strategyNotes: notes || '' });
  };

  // Persist strategy notes alongside cards + name in a single save.
  // Treats the side-panel's Save Notes button as a normal deck save.
  // works whether the deck is brand new or already on the server, and
  // it correctly bumps updated_at so other players see the new edit
  // timestamp.
  handleSaveStrategyNotes = async (notes) => {
    if (this.state.chosenCards.length === 0) {
      toast(t('Add at least one card before saving notes'));
      return;
    }
    this.setState({ strategyNotes: notes || '' }, () => {
      this.handleSave();
    });
  };

  handleToggleLikeFromEditor = async () => {
    const sourceId = this.props.deck?.sourceLibraryId || this.state.deckId;
    if (!sourceId || this.state.libraryToggleBusy) return;
    this.setState({ libraryToggleBusy: true });
    try {
      const result = await toggleDeckLike(sourceId);
      this.setState({
        libraryLiked: !!result.liked,
        libraryLikes: result.likes || 0,
        libraryToggleBusy: false,
      });
    } catch (err) {
      this.setState({ libraryToggleBusy: false });
      toast.error(t('Failed to like deck'));
    }
  };

  renderGameModeToggle() {
    return (
      <div className="shrink-0" style={{ width: '9cqw' }}>
        <Select
          portalMenu
          ariaLabel={t('Game Mode')}
          options={gameModeOptions().map((o) => ({ ...o, label: t(o.label), description: t(o.description) }))}
          value={this.state.gameMode}
          onValueChange={this.setGameMode}
          placeholder={t('Game Mode')}
        />
      </div>
    );
  }

  renderSaveBlockedTooltip(illegalCount, ruleInvalidCount = 0, unownedCount = 0) {
    const rect = this.state.saveBlockedHoverRect;
    if (!rect) return null;
    // Precedence in the tooltip when multiple save blockers are
    // active: rule violations first (Imposter extras. Structural,
    // must be fixed regardless of format), then peasant-format
    // illegality, then unowned cards. Each resolved blocker reveals
    // the next message so the user always sees the most actionable
    // issue first.
    const showRuleTooltip = ruleInvalidCount > 0;
    const showPeasantTooltip = !showRuleTooltip && illegalCount > 0;
    const showUnownedTooltip = !showRuleTooltip && !showPeasantTooltip && unownedCount > 0;

    // Canvas-local positioning. The .app-canvas wrapper has a
    // transform, which makes it the containing block for fixed
    // descendants. So left/top here are measured from the canvas,
    // not the viewport. Matches the pattern documented in
    // CLAUDE.md and used by SkillTreeScreen + DeckEditorSidebar's
    // peasant-illegal hover tooltip. The Save button lives in the
    // top-right header, so the tooltip defaults to BELOW the button
    // (there's typically no room above), and centers horizontally
    // on the button with canvas-edge clamping.
    const canvasEl = typeof document !== 'undefined' ? document.querySelector('.app-canvas') : null;
    const canvasRect = canvasEl
      ? canvasEl.getBoundingClientRect()
      : { left: 0, top: 0, width: typeof window !== 'undefined' ? window.innerWidth : 1600, height: typeof window !== 'undefined' ? window.innerHeight : 900 };
    const canvasScale = canvasRect.width / 1600;

    const TIP_WIDTH_DESIGN = 260;
    const TIP_MARGIN_DESIGN = 10;
    const TIP_PAD_DESIGN = 12;
    const TIP_HEIGHT_EST_DESIGN = 100;

    const tipWidth = TIP_WIDTH_DESIGN * canvasScale;
    const tipMargin = TIP_MARGIN_DESIGN * canvasScale;
    const tipPad = TIP_PAD_DESIGN * canvasScale;
    const tipHeightEst = TIP_HEIGHT_EST_DESIGN * canvasScale;

    const localTop = rect.top - canvasRect.top;
    const localBottom = rect.bottom - canvasRect.top;
    const localCenterX = rect.left + rect.width / 2 - canvasRect.left;

    const spaceBelow = Math.max(0, canvasRect.height - localBottom - tipPad);
    const spaceAbove = Math.max(0, localTop - tipPad);
    const placeAbove = spaceBelow < tipHeightEst + tipMargin && spaceAbove > spaceBelow;
    const localTooltipTop = placeAbove
      ? Math.max(tipPad, localTop - tipHeightEst - tipMargin)
      : localBottom + tipMargin;

    let localTooltipLeft = localCenterX - tipWidth / 2;
    const maxLeft = canvasRect.width - tipWidth - tipPad;
    if (localTooltipLeft > maxLeft) localTooltipLeft = maxLeft;
    if (localTooltipLeft < tipPad) localTooltipLeft = tipPad;

    // Unowned cards share the red theme with rule violations. Both
    // are blockers tied to the deck contents (vs. peasant's orange,
    // which signals a format-rarity issue). Picking the palette off
    // the same boolean keeps the visual association consistent with
    // the row-level tint in the sidebar.
    const useRedTheme = showRuleTooltip || showUnownedTooltip;

    return (
      <div
        role="tooltip"
        style={{
          position: 'fixed',
          left: localTooltipLeft,
          top: localTooltipTop,
          width: `${tipWidth}px`,
          pointerEvents: 'none',
          zIndex: 90,
          padding: '0.5cqw 0.75cqw',
          background: useRedTheme
            ? 'linear-gradient(180deg, rgba(28,8,8,0.98) 0%, rgba(20,4,4,0.98) 100%)'
            : 'linear-gradient(180deg, rgba(28,18,8,0.98) 0%, rgba(20,12,4,0.98) 100%)',
          border: useRedTheme
            ? '1px solid rgba(220, 80, 80, 0.55)'
            : '1px solid rgba(220, 140, 40, 0.55)',
          borderRadius: 'var(--radius-md)',
          boxShadow: useRedTheme
            ? '0 0.5cqw 1.25cqw rgba(0,0,0,0.55), 0 0 1.25cqw rgba(220,80,80,0.18), inset 0 1px 0 rgba(255, 140, 140, 0.08)'
            : '0 0.5cqw 1.25cqw rgba(0,0,0,0.55), 0 0 1.25cqw rgba(220,140,40,0.18), inset 0 1px 0 rgba(255, 220, 140, 0.08)',
        }}
      >
        <div
          className="text-[0.625cqw] font-semibold"
          style={{ color: useRedTheme ? '#ff9090' : '#f0b260' }}
        >
          {showRuleTooltip
            ? t('Cannot save deck')
            : showUnownedTooltip
              ? t('Not legal in Constructed')
              : t('Not legal in Peasant')}
        </div>
        <div
          className="text-[0.6875cqw] mt-1 leading-snug"
          style={{ color: TEXT_BODY }}
        >
          {showRuleTooltip
            ? (ruleInvalidCount === 1
                ? t('1 avatar in this deck is invalid. Only the Imposter avatar can carry extra avatars in your spellbook or collection. Re-equip Imposter or remove the extra to save.')
                : t('{n} avatars in this deck are invalid. Only the Imposter avatar can carry extra avatars in your spellbook or collection. Re-equip Imposter or remove them to save.', { n: ruleInvalidCount }))
            : showUnownedTooltip
              ? (unownedCount === 1
                  ? t("1 card in this deck isn't owned. Acquire it or switch to Sandbox before saving.")
                  : t("{n} cards in this deck aren't owned. Acquire them or switch to Sandbox before saving.", { n: unownedCount }))
              : (illegalCount === 1
                  ? t('1 card in this deck is not legal in Peasant. Remove it before saving.')
                  : t('{n} cards in this deck are not legal in Peasant. Remove them before saving.', { n: illegalCount }))}
        </div>
      </div>
    );
  }

  // ── Phone long-press gestures ─────────────────────────────────────
  // Hold a collection tile or deck row → full-card peek. Move while
  // holding → the peek becomes a drag ghost and the deck panel's zone
  // sections turn into drop targets. Release over a legal zone → add
  // (collection) or move (deck row). Tap behavior is untouched.
  _phonePress = new PhoneLongPress();
  _phoneDragData = null; // { card, printing, allowedZones, drop, dragging, x, y }

  _setPhoneFilterSlot = (el) => {
    if (el && el !== this.state.phoneFilterSlot) this.setState({ phoneFilterSlot: el });
  };

  phoneAllowedZonesForCard(card) {
    if (card.type === 'Avatar') return [];
    const isSite = card.type === 'Site' || card.played_horizontally;
    return isSite ? ['atlas', 'collection'] : ['spellbook', 'collection'];
  }

  beginPhoneTilePress = (e, card, printing) => {
    this._beginPhonePress(e, card, printing, this.phoneAllowedZonesForCard(card), (zone) => {
      this.handleAddCardToZone(card, zone, printing);
    });
  };

  beginPhoneRowPress = (e, row, sourceZone) => {
    const isSite = row.card?.type === 'Site' || row.card?.played_horizontally;
    const allowed = ['spellbook', 'atlas', 'collection'].filter((z) => canMoveToZone(sourceZone, z, isSite));
    this._beginPhonePress(e, row.card, row.printing, allowed, (zone) => {
      this.handleChangeZone(row.card.unique_id, zone, row.artGroup, row.foiling);
    });
  };

  _beginPhonePress(e, card, printing, allowedZones, drop) {
    this._phonePress.begin(e, {
      onActivate: () => {
        this._phoneDragData = { card, printing, allowedZones, drop, dragging: false, x: 0, y: 0 };
        this.setState({ phonePeek: { card, printing } });
      },
      onMove: (x, y) => this._phoneDragMove(x, y),
      onEnd: (x, y, wasActive) => this._phoneDragEnd(x, y, wasActive),
    });
  }

  _phoneZoneAt(x, y) {
    for (const el of document.querySelectorAll('[data-deck-zone]')) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        return el.getAttribute('data-deck-zone');
      }
    }
    return null;
  }

  _positionPhoneGhost(el) {
    const d = this._phoneDragData;
    if (!el || !d) return;
    el.style.left = `${d.x - 28}px`;
    el.style.top = `${d.y - 44}px`;
  }

  _phoneDragMove(x, y) {
    const d = this._phoneDragData;
    if (!d) return;
    d.x = x;
    d.y = y;
    if (!d.dragging) {
      d.dragging = true;
      playUI(UI.DRAG_START);
      this.setState({ phonePeek: null, phoneDragging: true, phoneDragOverZone: null });
    }
    this._positionPhoneGhost(this._phoneGhostEl);
    const zone = this._phoneZoneAt(x, y);
    const over = zone && d.allowedZones.includes(zone) ? zone : null;
    if (over !== this.state.phoneDragOverZone) this.setState({ phoneDragOverZone: over });
  }

  _phoneDragEnd(x, y, wasActive) {
    const d = this._phoneDragData;
    this._phoneDragData = null;
    if (this.state.phonePeek || this.state.phoneDragging || this.state.phoneDragOverZone) {
      this.setState({ phonePeek: null, phoneDragging: false, phoneDragOverZone: null });
    }
    if (!wasActive || !d || !d.dragging || x == null) return;
    const zone = this._phoneZoneAt(x, y);
    if (zone && d.allowedZones.includes(zone)) {
      playUI(UI.DRAG_DROP);
      d.drop(zone);
    }
  }

  // Phone layout: one-row header (back / name / overflow / save), the
  // collection filling the screen, and MobileDeckPanel as a collapsible
  // right rail. Desktop-only chrome (hover previews, save-blocked hover
  // tooltip, tutorial) is omitted; blocked saves toast instead.
  renderPhone({ canSave, formatIllegalCount, ruleInvalidCount, unownedCount, isLibraryView, saveLabel, author, updatedAt, commentsDeckId, commentsEnabled, canEditStrategy, ownedMap }) {
    const { sorceryCards, arenaProfile } = this.props;
    const {
      chosenCards, deckName, isSaving, showStats, showUnsavedPrompt, gameMode,
      strategyNotes, showSidePanel, sidePanelInitialTab, contentReady, gateMounted,
    } = this.state;
    const showPhoneMenu = !!this.state.showPhoneMenu;

    const handleSaveTap = () => {
      if (isSaving) return;
      if (chosenCards.length === 0) {
        this.showToast(t('Add at least one card before saving'));
        return;
      }
      if (ruleInvalidCount > 0) {
        this.showToast(ruleInvalidCount === 1
          ? t('1 avatar in this deck is invalid. Re-equip Imposter or remove it to save.')
          : t('{n} avatars in this deck are invalid. Re-equip Imposter or remove them to save.', { n: ruleInvalidCount }));
        return;
      }
      if (formatIllegalCount > 0) {
        this.showToast(formatIllegalCount === 1
          ? t('1 card in this deck is not legal in Peasant. Remove it before saving.')
          : t('{n} cards in this deck are not legal in Peasant. Remove them before saving.', { n: formatIllegalCount }));
        return;
      }
      if (unownedCount > 0) {
        this.showToast(unownedCount === 1
          ? t("1 card in this deck isn't owned. Acquire it or switch to Sandbox before saving.")
          : t("{n} cards in this deck aren't owned. Acquire them or switch to Sandbox before saving.", { n: unownedCount }));
        return;
      }
      this.handleSave();
    };

    const menuItem = (label, onClick) => (
      <button
        type="button"
        className="w-full text-left px-4 text-xs font-semibold"
        style={{ color: TEXT_PRIMARY, height: '2.75rem', background: 'none', border: 'none' }}
        onClick={() => { this.setState({ showPhoneMenu: false }); onClick(); }}
      >
        {label}
      </button>
    );

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden select-none" style={{ background: 'url("/deck-builder-bg-dimmed.webp") center no-repeat, #08080a', backgroundSize: 'cover' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        {gateMounted ? (
          <div
            className={contentReady
              ? 'entrance-gate is-closing pointer-events-none absolute inset-0 z-30 grid place-items-center'
              : 'entrance-gate absolute inset-0 z-30 grid place-items-center'}
            role="status"
            aria-live="polite"
          >
            <RuneSpinner size={56} />
          </div>
        ) : null}

        {/* Header row. fixed inset-0 spans the canvas' padding box, so
            each edge re-applies its own safe-area inset. */}
        <div
          className={contentReady ? 'stagger-in relative z-20 shrink-0 flex items-center gap-2 pb-1' : 'entrance-held relative z-20 shrink-0 flex items-center gap-2 pb-1'}
          style={{
            paddingLeft: 'max(0.625rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(0.625rem, env(safe-area-inset-right, 0px))',
            paddingTop: 'max(0.5rem, env(safe-area-inset-top, 0px))',
          }}
        >
          <BackButton
            title={t('Back to Decks')}
            onClick={this.handleBack}
            style={{ width: '2.5rem', height: '2.5rem' }}
          />
          {isLibraryView ? (
            <div className="flex items-baseline gap-2 shrink-0 min-w-0" style={{ maxWidth: '11rem' }}>
              <div className="arena-heading text-sm font-bold truncate" style={{ color: TEXT_PRIMARY }}>{deckName || t('Untitled Deck')}</div>
            </div>
          ) : (
            <input
              type="text"
              value={deckName}
              placeholder={t('Untitled Deck')}
              className="arena-heading text-sm font-bold px-3 shrink outline-none"
              style={{ ...INPUT_STYLE, color: TEXT_PRIMARY, borderRadius: 'var(--radius-lg)', height: '2.5rem', width: '9rem', minWidth: '5rem' }}
              onInput={(e) => this.setState({ deckName: e.target.value })}
            />
          )}
          {/* CardFilterBar (owned by DeckEditorCollection) portals its
              phone search strip into this slot so the header and the
              filter row share one line. The ref MUST be a stable method:
              an inline arrow gets re-invoked (null, then el) on every
              render, and a setState inside that is an infinite loop. */}
          <div
            ref={this._setPhoneFilterSlot}
            className="flex-1 min-w-0 flex items-center"
          />
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative">
              <button
                type="button"
                aria-label={t('Deck tools')}
                className="inline-flex items-center justify-center text-lg font-bold active:scale-[0.94]"
                style={{ ...BEVELED_BTN, width: '2.5rem', height: '2.5rem', borderRadius: 'var(--radius-lg)', color: TEXT_BODY, padding: 0 }}
                onClick={() => this.setState((s) => ({ showPhoneMenu: !s.showPhoneMenu }))}
              >
                &#8943;
              </button>
              {showPhoneMenu ? (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => this.setState({ showPhoneMenu: false })} />
                  <div
                    className="vh-menu-in absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden"
                    style={{
                      background: 'rgba(18, 14, 10, 0.98)',
                      border: `1px solid ${GOLD} 0.2)`,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                      minWidth: '13rem',
                    }}
                  >
                    {!isLibraryView ? (
                      <div className="px-3 pt-2.5 pb-1.5" style={{ borderBottom: `1px solid ${GOLD} 0.12)` }}>
                        <div className="text-[0.625rem] font-semibold mb-1" style={{ color: TEXT_MUTED }}>{t('Game Mode')}</div>
                        <Select
                          portalMenu
                          ariaLabel={t('Game Mode')}
                          options={gameModeOptions().map((o) => ({ ...o, label: t(o.label), description: t(o.description) }))}
                          value={gameMode}
                          onValueChange={this.setGameMode}
                          placeholder={t('Game Mode')}
                        />
                      </div>
                    ) : null}
                    {menuItem(t('Deck Statistics'), () => this.setState({ showStats: true }))}
                    {menuItem(t('Notes & Comments'), () => this.handleToggleSidePanel('strategy'))}
                    {menuItem(t('Copy for Curiosa'), this.handleCopyForCuriosa)}
                    {!isLibraryView ? menuItem(this.state.deckShared ? t('Unshare from Library') : t('Share to Deck Library'), this.handleToggleShare) : null}
                  </div>
                </>
              ) : null}
            </div>
            <button
              type="button"
              data-sound={UI.CONFIRM}
              className="px-4 text-sm font-bold inline-flex items-center justify-center active:scale-[0.96]"
              style={{ ...GOLD_BTN, height: '2.5rem', opacity: (!isSaving && !canSave) ? 0.5 : 1 }}
              onClick={handleSaveTap}
            >
              {isSaving ? <RuneSpinner size={20} /> : t('Save')}
            </button>
          </div>
        </div>

        {/* Main: collection + collapsible deck panel. No z-index here: a
            stacking context would trap the filter sheet's fixed z-[80]
            below the z-20 header. */}
        <div
          className={contentReady
            ? 'relative flex-1 flex min-h-0'
            : 'entrance-held relative flex-1 flex min-h-0'}
        >
          <div
            className="flex-1 min-w-0 py-1"
            style={{ paddingLeft: 'max(0.5rem, env(safe-area-inset-left, 0px))', paddingRight: '0.5rem' }}
          >
            <DeckEditorCollection
              sorceryCards={sorceryCards}
              collection={arenaProfile?.collection}
              ownedMap={ownedMap}
              chosenCards={chosenCards}
              gameMode={gameMode}
              onAddCard={this.handleAddCard}
              onAddCardToZone={this.handleAddCardToZone}
              onRemoveCard={this.handleDecrement}
              onShowToast={this.showToast}
              isArenaMode={!!arenaProfile}
              getSidebarHovered={this.getSidebarHovered}
              entranceReady={contentReady}
              onReady={this.handleContentReady}
              onTilePress={this.beginPhoneTilePress}
              filterSlot={this.state.phoneFilterSlot}
            />
          </div>
          <MobileDeckPanel
            chosenCards={chosenCards}
            ownedMap={ownedMap}
            gameMode={gameMode}
            invalidEntries={getInvalidEntries(chosenCards, this.getCurrentAvatar())}
            onIncrement={this.handleIncrement}
            onDecrement={this.handleDecrement}
            onInspect={this.handleSidebarInspect}
            onRowPress={this.beginPhoneRowPress}
            dragActive={!!this.state.phoneDragging}
            dragOverZone={this.state.phoneDragOverZone}
            dragAllowedZones={this._phoneDragData?.allowedZones}
          />
        </div>

        {/* Long-press peek: full-size card render, dismissed on release. */}
        {this.state.phonePeek ? (() => {
          const peek = this.state.phonePeek;
          const isSite = peek.card.type === 'Site' || peek.card.played_horizontally;
          return (
            <div className="fixed inset-0 z-[90] pointer-events-none flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
              <img
                src={getImageUrl({ card: peek.card, printing: peek.printing })}
                alt={peek.card.name}
                draggable={false}
                style={{
                  height: '86%',
                  aspectRatio: '63 / 88',
                  borderRadius: RADIUS.card,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
                  transform: isSite ? 'rotate(90deg)' : 'none',
                }}
              />
            </div>
          );
        })() : null}

        {/* Drag ghost following the finger. Position is written directly
            to the element (not state) so a drag doesn't re-render the
            editor on every pointermove. */}
        {this.state.phoneDragging && this._phoneDragData ? (
          <div
            ref={(el) => { this._phoneGhostEl = el; this._positionPhoneGhost(el); }}
            className="fixed z-[95] pointer-events-none"
            style={{ width: '3.5rem' }}
          >
            <img
              src={getImageUrl({ card: this._phoneDragData.card, printing: this._phoneDragData.printing })}
              alt=""
              draggable={false}
              style={{ width: '100%', borderRadius: RADIUS.card, boxShadow: '0 10px 26px rgba(0,0,0,0.7)' }}
            />
          </div>
        ) : null}

        {showStats && (
          <DeckStatisticsOverlay
            chosenCards={chosenCards}
            deckName={deckName}
            onClose={() => this.setState({ showStats: false })}
          />
        )}

        {showUnsavedPrompt && (
          <UnsavedChangesDialog
            onCancel={() => this.setState({ showUnsavedPrompt: false })}
            onDiscard={() => { this.setState({ showUnsavedPrompt: false }); this.props.onBack(); }}
            onSave={this.handleSaveAndLeave}
          />
        )}

        <DeckSidePanel
          open={showSidePanel}
          onClose={() => this.setState({ showSidePanel: false })}
          deckId={commentsDeckId}
          deckName={deckName}
          deckElements={this.getDeckElements()}
          deckOwnerId={author?.playerId || arenaProfile?.id || null}
          currentPlayerId={arenaProfile?.id || null}
          commentsEnabled={commentsEnabled}
          canEditStrategy={canEditStrategy}
          strategyNotes={strategyNotes}
          onStrategyNotesChange={canEditStrategy ? this.handleStrategyNotesChange : null}
          onSaveStrategy={canEditStrategy ? this.handleSaveStrategyNotes : null}
          initialTab={sidePanelInitialTab}
          highlightCommentId={this.props.highlightCommentId}
          sorceryCards={sorceryCards}
          onOpenCard={this.handleOpenCardFromNotes}
          onOpenProfile={this.props.onViewProfile}
          author={isLibraryView ? author : {
            playerId: arenaProfile?.id || null,
            playerName: arenaProfile?.name || null,
            playerAvatar: arenaProfile?.profileAvatar || null,
            playerTier: arenaProfile?.tier || null,
          }}
        />

        {this.state.notesInspectorCard ? (
          <CardInspector
            card={this.state.notesInspectorCard}
            imageUrl={this.state.notesInspectorCard.printings?.[0]?.image_url}
            rarity={this.state.notesInspectorCard.rarity}
            foiling={this.state.notesInspectorCard.printings?.[0]?.foiling}
            onClose={this.handleCloseNotesInspector}
          />
        ) : null}

        {/* Deck-panel row tap opens the same inspector Space does on desktop. */}
        {this.state.sidebarInspect ? (
          <CardInspector
            card={this.state.sidebarInspect.card}
            imageUrl={this.state.sidebarInspect.printing?.image_url}
            rarity={this.state.sidebarInspect.card?.rarity}
            foiling={this.state.sidebarInspect.printing?.foiling}
            onClose={this.handleCloseSidebarInspect}
          />
        ) : null}
      </div>
    );
  }

  render() {
    const { sorceryCards, arenaProfile, onToggleMailbox, mailboxUnreadCount, mailboxDropdown, onToggleFriends, friendListData, deck: deckProp } = this.props;
    const {
      chosenCards, deckName, isSaving, showStats, showUnsavedPrompt, gameMode,
      strategyNotes, showSidePanel, sidePanelInitialTab,
      libraryLiked, libraryLikes, deckShared, deckId, contentReady, gateMounted,
    } = this.state;
    const isArenaMode = !!arenaProfile;
    const ownedMap = this._getOwnedMap();
    // Cards illegal for the deck's currently-selected format. Mirrors
    // the deck-wide rule that runs inside validateDeckStructure at
    // save time. Every zone counts (spellbook, atlas, collection
    // sideboard), only Avatar entries are exempt (rarity-less). The
    // save gate and this banner stay in lockstep so the user can't
    // see "0 illegal" here and still hit a save rejection.
    const formatIllegalCards = gameMode === 'peasant'
      ? chosenCards.filter((e) =>
          e.card?.type !== 'Avatar'
          && !isPeasantLegal(e.card)
        )
      : [];
    const formatIllegalCount = formatIllegalCards.length;
    // Structural rule violations (currently only: extra avatars in a
    // non-Imposter deck). Same gating treatment as peasant-illegal.
    // block save and surface a tooltip on the disabled button.
    const ruleInvalidIds = getInvalidEntries(chosenCards, this.getCurrentAvatar());
    const ruleInvalidCount = ruleInvalidIds.size;
    // Ownership shortfall for constructed mode. Constructed requires
    // owning every card in the deck; sandbox is exempt by definition,
    // and peasant intentionally lets the player save a deck containing
    // cards they haven't acquired yet (peasant only enforces rarity at
    // save time. Play-time ownership is gated separately).
    const unownedCount = gameMode === 'constructed'
      ? getMissingCards(chosenCards, ownedMap).count
      : 0;
    const canSave = chosenCards.length > 0 && !isSaving
      && formatIllegalCount === 0
      && ruleInvalidCount === 0
      && unownedCount === 0;

    const isLibraryView = !!deckProp?.libraryView;
    const author = deckProp?.author || null;
    const updatedAt = deckProp?.updatedAt;
    // Library view uses the source library deck id (state.deckId is
    // blank in library view because Save creates a new copy). Owner
    // view uses the deck's own id.
    const commentsDeckId = isLibraryView ? (deckProp?.sourceLibraryId || null) : deckId;
    // Comments are only meaningful on decks visible to others. I.e.
    // currently shared, or being viewed from the library. Brand new
    // unsaved decks have no id either, so we hide the comments tab
    // there too.
    const commentsEnabled = !!commentsDeckId && (isLibraryView || deckShared);
    const canEditStrategy = !isLibraryView;
    const saveLabel = isLibraryView ? t('Save to My Library') : t('Save Deck');

    if (PHONE) {
      return this.renderPhone({
        canSave, formatIllegalCount, ruleInvalidCount, unownedCount,
        isLibraryView, saveLabel, author, updatedAt,
        commentsDeckId, commentsEnabled, canEditStrategy, ownedMap,
      });
    }

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden select-none" style={{ background: 'url("/deck-builder-bg-dimmed.webp") center no-repeat, #08080a', backgroundSize: '100% 100%' }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <StoreTorchFX />
        <AmbientParticles preset="deckbuilder" />

        {/* Loading gate. The background, torches and particles above are the
            stage and stay put; only the chrome waits. Once the art is warm it
            fades out over the first beats of the entrance cascade, dropping
            pointer-events immediately so no click is swallowed mid-fade. */}
        {gateMounted ? (
          <div
            className={contentReady
              ? 'entrance-gate is-closing pointer-events-none absolute inset-0 z-30 grid place-items-center'
              : 'entrance-gate absolute inset-0 z-30 grid place-items-center'}
            role="status"
            aria-live="polite"
          >
            <RuneSpinner size={72} />
          </div>
        ) : null}

        {/* ─── TOOLBAR ──────────────────────────────────────── */}
        <AppHeader
          noBorder
          className={contentReady ? 'stagger-in' : 'entrance-held'}
          profile={arenaProfile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxDropdown={mailboxDropdown}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
        >
          <BackButton title={t('Back to Decks')} onClick={this.handleBack} />
          {isLibraryView ? (
            <div className="flex items-baseline gap-2 flex-shrink-0 min-w-0" style={{ maxWidth: '24cqw' }}>
              <div className="arena-heading text-sm font-bold truncate" style={{ color: TEXT_PRIMARY }}>{deckName || t('Untitled Deck')}</div>
              <div className="text-[0.625cqw] shrink-0 flex items-baseline gap-1.5">
                <span style={{ color: ACCENT_GOLD }}>{t('by @{name}', { name: author?.playerName || t('Unknown') })}</span>
                {updatedAt && (
                  <span style={{ color: TEXT_MUTED }} title={new Date(updatedAt).toLocaleString()}>· {timeAgo(updatedAt)}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="relative flex-shrink-0">
              <input
                type="text"
                value={deckName}
                placeholder={t('Untitled Deck')}
                title={updatedAt ? t('Updated {date}', { date: new Date(updatedAt).toLocaleString() }) : undefined}
                className="peer arena-heading text-sm font-bold px-3 w-64 outline-none transition-colors duration-200 focus:border-[rgba(180,140,60,0.35)]"
                style={{
                  ...INPUT_STYLE,
                  color: TEXT_PRIMARY,
                  borderRadius: 'var(--radius-lg)',
                  height: '2.5cqw',
                  // Reserve a constant slot for the "Updated" suffix so the
                  // text never runs under it and never shifts on focus.
                  paddingRight: updatedAt ? '4.75cqw' : undefined,
                }}
                onInput={(e) => this.setState({ deckName: e.target.value })}
              />
              {/* Last-saved stamp lives INSIDE the input's right edge so the
                  name field stays a single 2.5cqw row aligned with the other
                  header controls. Fades out while editing. */}
              {updatedAt ? (
                <span
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[0.55cqw] pointer-events-none transition-opacity duration-150 peer-focus:opacity-0"
                  style={{ color: TEXT_MUTED }}
                  aria-hidden="true"
                >
                  {t('Updated {time}', { time: timeAgo(updatedAt) })}
                </span>
              ) : null}
            </div>
          )}
          {/* Right-aligned action cluster: mode picker, tool group, save.
              ml-auto pulls the whole cluster to the row's end with one tight
              internal gap, so the header reads as [identity ......... tools]
              instead of a strip of floating fragments. */}
          <div className="ml-auto flex items-center gap-2 shrink-0">
          {/* Game-mode toggle is owner-only. In library view the mode
              is irrelevant. It gets auto-picked on Save to My Library
              based on whether the player owns every card. */}
          {!isLibraryView && this.renderGameModeToggle()}
          {isLibraryView && (
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-all"
              style={{
                color: libraryLiked ? '#e85050' : TEXT_MUTED,
                background: libraryLiked ? 'rgba(232,80,80,0.1)' : 'transparent',
                border: `1px solid ${libraryLiked ? 'rgba(232,80,80,0.3)' : 'rgba(255,255,255,0.08)'}`,
              }}
              onClick={this.handleToggleLikeFromEditor}
              title={libraryLiked ? t('Unlike') : t('Like deck')}
            >
              <span>{libraryLiked ? '♥' : '♡'}</span>
              <span>{libraryLikes}</span>
            </button>
          )}
          {/* Deck tools. One beveled segmented group (stats | notes | share)
              instead of three floating icon buttons. The share menu anchors
              to the group's wrapper because the group itself clips overflow. */}
          <div className="relative">
            <div className="vh-seg-group" style={{ ...BEVELED_BTN, height: '2.5cqw', padding: 0 }}>
              <Focusable id="deck-stats-btn" asChild onActivate={() => this.setState({ showStats: true })}>
              <button
                type="button"
                className="vh-seg-btn"
                onClick={() => this.setState({ showStats: true })}
                title={t('Deck statistics')}
                aria-label={t('Deck statistics')}
              >
                <IconChartBar style={{ width: '1cqw', height: '1cqw' }} />
              </button>
              </Focusable>
              <div className="vh-seg-divider" aria-hidden="true" />
              {/* Notes (strategy + comments) side panel toggle */}
              <button
                type="button"
                className="vh-seg-btn"
                data-active={showSidePanel ? 'true' : 'false'}
                onClick={() => this.handleToggleSidePanel('strategy')}
                title={t('Strategy notes & comments')}
                aria-label={t('Strategy notes & comments')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1cqw', height: '1cqw' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="14" y2="17" />
                </svg>
              </button>
              {/* Share. Only meaningful for the deck's owner */}
              {!isLibraryView && (
                <>
                  <div className="vh-seg-divider" aria-hidden="true" />
                  <button
                    type="button"
                    className="vh-seg-btn"
                    data-active={this.state.showShareMenu ? 'true' : 'false'}
                    onClick={() => this.setState((s) => ({ showShareMenu: !s.showShareMenu }))}
                    title={t('Share deck')}
                    aria-label={t('Share deck')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1cqw', height: '1cqw' }}>
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                      <polyline points="16 6 12 2 8 6" />
                      <line x1="12" y1="2" x2="12" y2="15" />
                    </svg>
                  </button>
                </>
              )}
            </div>
            {!isLibraryView && this.state.showShareMenu && (
              <div
                className="vh-menu-in absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden"
                style={{
                  background: 'rgba(18, 14, 10, 0.98)',
                  border: `1px solid ${GOLD} 0.2)`,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  minWidth: '12.5cqw',
                }}
              >
                <button
                  type="button"
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs transition-all text-left"
                  style={{ color: TEXT_PRIMARY }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={this.handleCopyForCuriosa}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }}>
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  {t('Copy for Curiosa')}
                </button>
                <button
                  type="button"
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs transition-all text-left"
                  style={{ color: this.state.deckShared ? '#e89090' : TEXT_PRIMARY }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  onClick={this.handleToggleShare}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }}>
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  {this.state.deckShared ? t('Unshare from Library') : t('Share to Deck Library')}
                </button>
              </div>
            )}
          </div>
          {/* Wrapper captures hover events while the inner button is
              disabled. `disabled` blocks pointer-events on the
              button itself, so mouseenter/leave have to live on a
              parent that stays interactive. The wrapper only stores
              the rect when there's actually a save-blocking reason
              the player needs explained (peasant illegality); empty
              deck and in-flight save are self-evident from the UI. */}
          <div
            className="inline-flex"
            onMouseEnter={(e) => {
              if (formatIllegalCount > 0 || ruleInvalidCount > 0 || unownedCount > 0) {
                this.setState({ saveBlockedHoverRect: e.currentTarget.getBoundingClientRect() });
              }
            }}
            onMouseLeave={() => {
              if (this.state.saveBlockedHoverRect) this.setState({ saveBlockedHoverRect: null });
            }}
          >
            <Focusable id="deck-save-btn" asChild onActivate={this.handleSave}>
            <button
              type="button"
              data-tutorial="deck-save"
              data-sound={UI.CONFIRM}
              className="px-5 text-sm font-bold transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] disabled:pointer-events-none inline-flex items-center justify-center"
              style={{ ...GOLD_BTN, height: '2.5cqw', opacity: (!isSaving && !canSave) ? 0.4 : 1 }}
              disabled={!canSave}
              onClick={this.handleSave}
            >
              {isSaving ? <span className="relative"><span className="invisible">{saveLabel}</span><span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={20} /></span></span> : saveLabel}
            </button>
            </Focusable>
          </div>
          </div>
        </AppHeader>
        {this.state.saveBlockedHoverRect && (formatIllegalCount > 0 || ruleInvalidCount > 0 || unownedCount > 0)
          ? this.renderSaveBlockedTooltip(formatIllegalCount, ruleInvalidCount, unownedCount)
          : null}

        {/* ─── MAIN CONTENT ─────────────────────────────────── */}
        <div
          className={contentReady
            ? 'relative z-10 flex-1 flex min-h-0'
            : 'entrance-held relative z-10 flex-1 flex min-h-0'}
        >
          {/* Collection browser */}
          <div data-tutorial="deck-collection" className="flex-1 min-w-0 px-4 py-2">
            <DeckEditorCollection
              sorceryCards={sorceryCards}
              collection={arenaProfile?.collection}
              ownedMap={ownedMap}
              chosenCards={chosenCards}
              gameMode={gameMode}
              onAddCard={this.handleAddCard}
              onAddCardToZone={this.handleAddCardToZone}
              onRemoveCard={this.handleDecrement}
              onShowToast={this.showToast}
              isArenaMode={isArenaMode}
              getSidebarHovered={this.getSidebarHovered}
              entranceReady={contentReady}
              onReady={this.handleContentReady}
            />
          </div>

          {/* Sidebar */}
          <div
            data-tutorial="deck-sidebar"
            className={contentReady
              ? 'stagger-in w-[18.75cqw] shrink-0 overflow-hidden h-full'
              : 'w-[18.75cqw] shrink-0 overflow-hidden h-full'}
            style={contentReady ? { '--stagger': '140ms' } : undefined}
          >
            <DeckEditorSidebar
              chosenCards={chosenCards}
              ownedMap={ownedMap}
              gameMode={gameMode}
              invalidEntries={getInvalidEntries(chosenCards, this.getCurrentAvatar())}
              onIncrement={this.handleIncrement}
              onDecrement={this.handleDecrement}
              onChangeZone={this.handleChangeZone}
              onCardHover={this.handleSidebarHover}
              onInspect={this.handleSidebarInspect}
              onInspectClose={this.handleCloseSidebarInspect}
              inspectorOpen={!!this.state.sidebarInspect}
            />
          </div>
        </div>

        {/* ─── STATS OVERLAY ─────────────────────────────────── */}
        {showStats && (
          <DeckStatisticsOverlay
            chosenCards={chosenCards}
            deckName={this.state.deckName}
            onClose={() => this.setState({ showStats: false })}
          />
        )}


        {/* ─── UNSAVED CHANGES DIALOG ───────────────────────── */}
        {showUnsavedPrompt && (
          <UnsavedChangesDialog
            onCancel={() => this.setState({ showUnsavedPrompt: false })}
            onDiscard={() => { this.setState({ showUnsavedPrompt: false }); this.props.onBack(); }}
            onSave={this.handleSaveAndLeave}
          />
        )}

        {/* Strategy / comments side panel */}
        <DeckSidePanel
          open={showSidePanel}
          onClose={() => this.setState({ showSidePanel: false })}
          deckId={commentsDeckId}
          deckName={deckName}
          deckElements={this.getDeckElements()}
          deckOwnerId={author?.playerId || arenaProfile?.id || null}
          currentPlayerId={arenaProfile?.id || null}
          commentsEnabled={commentsEnabled}
          canEditStrategy={canEditStrategy}
          strategyNotes={strategyNotes}
          onStrategyNotesChange={canEditStrategy ? this.handleStrategyNotesChange : null}
          onSaveStrategy={canEditStrategy ? this.handleSaveStrategyNotes : null}
          initialTab={sidePanelInitialTab}
          highlightCommentId={this.props.highlightCommentId}
          sorceryCards={sorceryCards}
          onOpenCard={this.handleOpenCardFromNotes}
          onOpenProfile={this.props.onViewProfile}
          // Author identity for the panel header. Library view uses
          // the author we got from the server; owner view uses the
          // current player's profile so the header reads as "their
          // deck" rather than going blank.
          author={isLibraryView ? author : {
            playerId: arenaProfile?.id || null,
            playerName: arenaProfile?.name || null,
            playerAvatar: arenaProfile?.profileAvatar || null,
            playerTier: arenaProfile?.tier || null,
          }}
        />

        {/* Card inspector for an @card link in the notes panel.
            Opens above the panel itself, so clicking through the
            pill always lands on a full-screen detailed view. */}
        {this.state.notesInspectorCard ? (
          <CardInspector
            card={this.state.notesInspectorCard}
            imageUrl={this.state.notesInspectorCard.printings?.[0]?.image_url}
            rarity={this.state.notesInspectorCard.rarity}
            foiling={this.state.notesInspectorCard.printings?.[0]?.foiling}
            onClose={this.handleCloseNotesInspector}
          />
        ) : null}

        {/* Sidebar hover card preview */}
        {this.renderSidebarPreview()}

        {/* Space-to-inspect from a sidebar row. Same hotkey the
            collection grid uses, so both halves of the editor inspect
            the same way. */}
        {this.state.sidebarInspect ? (
          <CardInspector
            card={this.state.sidebarInspect.card}
            imageUrl={this.state.sidebarInspect.printing?.image_url}
            rarity={this.state.sidebarInspect.card?.rarity}
            foiling={this.state.sidebarInspect.printing?.foiling}
            onClose={this.handleCloseSidebarInspect}
          />
        ) : null}

        {/* First-run deck builder onboarding overlay. */}
        {this.state.showDeckTutorial && (
          <TutorialOverlay
            steps={DECK_TUTORIAL_STEPS.map((s) => {
              const freePlay = isFreePlay();
              return {
                ...s,
                title: t(freePlay && s.freePlayTitle ? s.freePlayTitle : s.title),
                body: t(freePlay && s.freePlayBody ? s.freePlayBody : s.body),
              };
            })}
            onDismiss={this.handleDeckTutorialDismiss}
          />
        )}
      </div>
    );
  }
}
